/**
 * Montana HOA Form Service
 * Handles form submissions for Montana demo (montana.toolboxlogic.com)
 *
 * Migration Context:
 * - Replaces non-functional Supabase client with self-hosted PostgreSQL on DROPLET
 * - Submits to HOA API endpoints: /api/v1/hoa/*
 * - Schema: hoa_landingpage_all_cities on 64.23.193.194:5432
 * - Added: Dec 28, 2025 - Phase 3 of Montana migration
 */

const getApiBaseUrl = () => {
  if (import.meta.env.VITE_API_BASE_URL) return import.meta.env.VITE_API_BASE_URL;

  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (
      host.includes('montana.toolboxlogic.com') ||
      host.includes('montana-demo.toolboxlogic.com')
    ) {
      return 'https://api.toolboxlogic.com';
    }
  }

  return import.meta.env.PROD ? 'https://api.toolboxlogic.com' : 'http://localhost:3002';
};

const API_BASE_URL = getApiBaseUrl();

export interface ApiResponse<T = Record<string, unknown>> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

export interface OnboardingFormData {
  // Basic info (required)
  community_name: string;
  community_city?: string;
  community_state?: string;
  community_location?: string;
  total_units: string;
  year_established?: number;
  community_type: string;
  contact_name: string;
  contact_email: string;
  contact_phone?: string;
  contact_title?: string;

  // Financial data
  treasury_amount?: string;
  monthly_hoa_fees?: string;
  annual_budget?: string;
  reserve_funds?: string;
  outstanding_debts?: string;
  investment_accounts?: boolean;
  banking_relationships?: unknown;
  budget_status?: string;

  // Governance
  governance?: unknown;
  governance_experience?: string;
  other_governance_structure?: string;
  time_commitment?: string;
  governance_comments?: string;

  // Software
  current_software?: unknown;
  other_software?: string;
  pain_points?: unknown;
  previous_failures?: string;
  additional_challenges?: string;
  pain_severity?: string;
  integration_needs?: unknown;

  // Features & goals
  current_features?: unknown;
  wishlist_features?: unknown;
  top_priority?: string;
  custom_feature_requests?: string;
  goals?: unknown;
  community_assets?: unknown;
  success_metrics?: unknown;
  specific_objectives?: string;
  implementation_timeline?: string;
  compliance_needs?: unknown;
  seasonal_needs?: unknown;

  // Budget & Timeline
  budget_range?: string;
  current_budget?: string;
  timeline?: string;
  timeline_drivers?: unknown;
  urgency?: string;
  decision_process?: string;
  decision_makers?: unknown;
  competitive_analysis?: string;
  decision_timeline?: string;
  evaluation_criteria?: unknown;
  budget_flexibility?: string;
  budget_notes?: string;

  // Interests & Engagement
  interests?: unknown;
  progressive_interests?: unknown;
  communication_preferences?: unknown;
  engagement_level?: string;
  event_preferences?: unknown;
  additional_interests?: string;

  // Referral + consent + notes
  heard_about_us?: unknown;
  referrer_info?: string;
  willing_to_share?: boolean;
  additional_info?: string;
  sales_notes?: string;

  // Recommendation
  recommended_tier?: string;
  recommendation_reasoning?: unknown;
  selected_tier?: string;
  estimated_value?: number;
  form_version?: string;

  // Sales tracking
  status?: string;
  lead_score?: number;
  assigned_to?: string;
  demo_scheduled_at?: string;

  // Progressive disclosure + validation
  progressive_disclosure_data?: unknown;
  validation_errors?: unknown;
  current_step?: number;
  completed_steps?: unknown;

  // Client/device metadata
  browser_fingerprint?: string;
  device_info?: unknown;
  completion_time_seconds?: number;
  last_activity_at?: string;

  // Metadata
  form_session_data?: unknown;
  user_agent?: string;
  referrer?: string;
  utm_data?: unknown;
}

export interface PricingAssessmentData {
  // Required
  selected_tier: string;
  community_name: string;
  total_units: string;
  community_type: string;
  contact_name: string;
  contact_email: string;
  contact_phone?: string;

  // Assessment fields
  selected_addons?: string[];
  budget_range?: string;
  urgency?: string;
  decision_process?: string;
  budget_flexibility?: string;

  // Features & goals
  wishlist_features?: unknown;
  goals?: unknown;
  success_metrics?: unknown;

  // Source tracking
  source?: string;
  referrer?: string;
  utm_data?: unknown;
}

export interface StakeholderContactData {
  // Link to parent submission
  submission_id?: string;
  assessment_id?: string;

  // Stakeholder details (required)
  name: string;
  title?: string;
  email: string;
  phone?: string;
  role?: string;

  // Decision-making info
  influence_level?: string;
  decision_authority?: string;

  // Contact preferences
  contact_preference?: string;
  preferred_contact_method?: string; // Legacy/Alternative
  best_time_to_contact?: string; // Legacy/Alternative
  notes?: string;
}

/**
 * Montana HOA Form Service
 * Handles all form submissions for Montana demo
 */
export const montanaHOAFormService = {
  /**
   * Submit full HOA onboarding form (76 fields)
   * Used by: CommunityOnboardingPage, onboarding wizard steps 1-8
   */
  async submitOnboardingForm(formData: OnboardingFormData): Promise<ApiResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/hoa/onboarding-submission`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.message || 'Failed to submit onboarding form',
          error: data.error || `HTTP ${response.status}`,
        };
      }

      return {
        success: true,
        message: data.message || 'Onboarding form submitted successfully',
        data: data.data,
      };
    } catch (error) {
      console.error('[Montana HOA] Onboarding submission error:', error);
      return {
        success: false,
        message: 'Network error - please try again',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  /**
   * Submit pricing assessment form (45 fields)
   * Used by: Pricing tier selection, cost assessment wizard
   */
  async submitPricingAssessment(assessmentData: PricingAssessmentData): Promise<ApiResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/hoa/pricing-assessment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(assessmentData),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.message || 'Failed to submit pricing assessment',
          error: data.error || `HTTP ${response.status}`,
        };
      }

      return {
        success: true,
        message: data.message || 'Pricing assessment submitted successfully',
        data: data.data,
      };
    } catch (error) {
      console.error('[Montana HOA] Pricing assessment error:', error);
      return {
        success: false,
        message: 'Network error - please try again',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  /**
   * Submit stakeholder contact info (13 fields)
   * Used by: Key stakeholder forms, contact collection
   */
  async submitStakeholderContact(stakeholderData: StakeholderContactData): Promise<ApiResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/hoa/stakeholder-contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(stakeholderData),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.message || 'Failed to submit stakeholder contact',
          error: data.error || `HTTP ${response.status}`,
        };
      }

      return {
        success: true,
        message: data.message || 'Stakeholder contact submitted successfully',
        data: data.data,
      };
    } catch (error) {
      console.error('[Montana HOA] Stakeholder contact error:', error);
      return {
        success: false,
        message: 'Network error - please try again',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  /**
   * Health check for HOA API
   * Verifies API is reachable before form submission
   */
  async healthCheck(): Promise<ApiResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/health`);
      const data = await response.json();
      return {
        success: response.ok,
        message: data.message || 'API is healthy',
        data,
      };
    } catch (error) {
      return {
        success: false,
        message: 'API health check failed',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },
};
